// Adivaa Protection Plan — Terms & Conditions copy.
// Source of truth: "Adivaa Accidental Plan TnCs page" design export. Keep wording exact.

import {
  clause,
  contactSection,
  dash,
  note,
  slaSection,
  step,
  type TncHero,
  type TncSection,
} from "@/components/tnc/tncTypes";

export const adivaaTncHero: TncHero = {
  breadcrumb: "Adivaa Protection Plan",
  current: "Terms & Conditions",
  title: "Terms and Conditions",
  lines: [
    "Accidental Damage Protection Plan",
    "For products purchased on Adivaa Smart.",
  ],
};

export const adivaaTncFooterNote =
  "Adivaa Protection Plan is administered by EmbedMike India Private Limited (Protega) on behalf of Adivaa Smart. It is a service plan, not an insurance policy, and the Terms and Conditions govern in full.";

export const adivaaTncSections: TncSection[] = [
  {
    kind: "clauses",
    id: "plan-introduction",
    title: "Plan introduction",
    blocks: [
      clause(
        "The Protega × ADIVAA SMART Accidental Damage Protection Plan (“Plan”) is a post-purchase service contract designed to provide protection against sudden, unforeseen and unintentional physical damage to eligible products arising from external events during the course of normal personal use, subject to these Terms and Conditions (“Terms”)."
      ),
      clause(
        "This Plan is administered by EmbedMike India Private Limited (“Protega”) on behalf of ADIVAA SMART and shall at all times be construed as a service agreement and not as a contract of insurance. The benefits under this Plan are supplementary in nature and do not replace, overlap or extend the manufacturer’s warranty obligations, seller guarantees, or statutory consumer rights available under applicable laws. Protega may appoint Authorised Service Partners to perform any part of the Plan and remains responsible to the Customer for its obligations under the Plan."
      ),
      clause(
        "Coverage under this Plan is limited strictly to accidental damage events that are external, visible and verifiable in nature, and which directly result in functional impairment, operational failure or structural damage to the covered product. Any damage that cannot be reasonably attributed to a defined accidental event shall not be eligible for coverage under this Plan."
      ),
      clause(
        "The Plan becomes effective when the order for the Product and Plan is placed and remains valid for a continuous and uninterrupted period of either twelve (12) months or twenty-four (24) months, as selected at the time of purchase."
      ),
      clause(
        "This Plan is valid only within the territorial jurisdiction of India. All claim handling, inspection, diagnostics, logistics, repair, replacement and settlement activities shall be carried out solely through Protega’s authorised service and logistics network."
      ),
      clause(
        "Each approved claim under this Plan shall be subject to a mandatory deductible equal to ten percent (10%) of the Invoice Value or ₹500 (Indian Rupees Five Hundred), whichever is lower, or as stated in your Certificate of Protection. The applicable deductible must be paid by the customer prior to the initiation of any service fulfilment process and is non-refundable once the RMA is issued.",
        "Deductible:"
      ),
      clause(
        "Coverage under this Plan is conditional upon the product being used, operated, handled and stored strictly in accordance with the manufacturer’s specifications, user manuals, safety instructions and intended usage scenarios. Any deviation from prescribed usage conditions that contributes to the damage may result in denial of coverage."
      ),
      clause(
        "The Certificate of Protection, these Terms, the invoice for the Product and the Plan, and the payment confirmation together form the Plan. If the Certificate of Protection and these Terms differ, these Terms prevail.",
        "Documents forming the Plan:"
      ),
      clause(
        "Although cover starts when the order is placed, the Plan covers only accidental damage that occurs after the Product has been delivered to the Customer, while the Product is being used for its intended purpose in normal personal or household use. Any damage, loss or defect arising before or during shipping and delivery is not covered and is handled under the Merchant’s return and replacement policy.",
        "When damage is covered:"
      ),
      clause(
        "Where a claim is resolved by replacement or Store Credit, depreciation based on the age of the Product from the invoice date applies instead of the deductible, as set out below. Store Credit is issued for the Invoice Value less depreciation. For a replacement, the Customer pays the depreciation amount before the replacement is dispatched, or may choose Store Credit instead. Any deductible already paid is adjusted against the depreciation amount or added to the Store Credit. Claims resolved by repair are not subject to depreciation.",
        "Depreciation on replacement or store credit:"
      ),
      {
        kind: "table",
        head: ["Age of product at claim", "Depreciation"],
        rows: [
          ["Up to 6 months", "10%"],
          ["6 to 12 months", "20%"],
          ["12 to 18 months", "30%"],
          ["18 to 24 months", "40%"],
        ],
      },
    ],
  },
  {
    kind: "clauses",
    id: "customer-responsibilities",
    title: "Customer responsibilities",
    blocks: [
      note(
        "In order to remain eligible for coverage and services under this Plan, the customer agrees to comply with the following responsibilities and obligations:"
      ),
      clause(
        "Retain and provide valid proof of purchase, including the original invoice and Plan confirmation documentation, at the time of claim."
      ),
      clause(
        "Notify Protega of any accidental damage incident within seventy-two (72) hours of occurrence."
      ),
      clause(
        "Provide complete, accurate and truthful information regarding the nature, cause and circumstances of the damage."
      ),
      clause(
        "Take all reasonable steps to prevent further damage to the product following the incident."
      ),
      clause(
        "Immediately discontinue use of the product upon occurrence of damage that may impact functionality or safety."
      ),
      clause(
        "Ensure that the product is not opened, repaired, modified or tampered with by any unauthorised third party or service provider, and do not hand the product to any service centre before Protega approves the claim."
      ),
      clause(
        "Use the product strictly in accordance with the manufacturer’s operating instructions and intended use conditions."
      ),
      clause(
        "Avoid exposure to environments, substances or conditions that may reasonably be expected to cause damage."
      ),
      clause(
        "Remove or back up all personal data and information prior to handing over the product for inspection, pickup or service."
      ),
      clause(
        "Cooperate fully with Protega and its authorised partners during the claim assessment, pickup, diagnostics and resolution process, and hand over the product complete with all parts supplied with it."
      ),
      clause(
        "Keep your registered email address, mobile number and WhatsApp number up to date."
      ),
      note(
        "Failure to comply with any of the above responsibilities may result in rejection of the claim or denial of services under this Plan."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "how-to-file-a-claim",
    title: "How to file a claim",
    blocks: [
      step("Step 1: Contact support"),
      note(
        "Customers must initiate a claim through any of the following official channels. Claims are accepted only through these two channels."
      ),
      dash("www.protega.ai/customer-login", "Portal:"),
      dash("+91 93240 95283", "WhatsApp:"),
      note(
        "All claims must be reported within seventy-two (72) hours from the time of occurrence of the accidental incident.",
        true
      ),
      step("Step 2: Submission of documentation"),
      note(
        "Customers shall be required to submit the following documents and information:"
      ),
      dash("Copy of the original purchase invoice"),
      dash("Proof of Plan purchase or confirmation"),
      dash(
        "Original, unedited photographs and/or videos clearly depicting the extent and nature of the damage"
      ),
      dash(
        "A detailed written description of the incident, including date, time, location and cause of damage"
      ),
      note(
        "Protega reserves the right to request any additional documentation, clarification or a live video inspection required for claim validation. A claim will not be processed until the requested information is provided."
      ),
      step("Step 3: Claim assessment and verification"),
      note(
        "All claims shall be subject to technical evaluation and validation, including but not limited to:"
      ),
      dash("Assessment of the type, cause and extent of damage"),
      dash("Verification of incident consistency and plausibility"),
      dash("Confirmation that the damage falls within covered events"),
      note(
        "Initial assessment is typically completed within 24–48 working hours from the receipt of complete documentation."
      ),
      step("Step 4: Deductible payment"),
      note(
        "Upon claim approval, the customer shall be required to pay the applicable deductible amount via a secure payment link shared by Protega."
      ),
      step("Step 5: Logistics and pickup"),
      note(
        "Upon successful payment of the deductible, a Return Merchandise Authorization (RMA) shall be generated, and the product shall be scheduled for pickup through Protega’s authorised logistics partners."
      ),
      step("Step 6: Repair, replacement or settlement"),
      note(
        "Based on the outcome of the technical assessment, Protega may, at its sole discretion:"
      ),
      dash("Repair the product using new or refurbished parts"),
      dash(
        "Replace the product with a new, refurbished or functionally equivalent unit"
      ),
      dash("Provide store credit up to the Invoice Value"),
      note(
        "Standard repair cases are typically completed within 5–7 working days; replacement cases are subject to product availability. All timelines are subject to clause 8."
      ),
      clause(
        "The product must not be repaired, opened or handed to any service centre before Protega approves the claim in writing. Costs incurred before approval will not be reimbursed, and such a claim may be rejected.",
        "Approval before repair:"
      ),
      clause(
        "The deductible must be paid within 7 days of the payment link being sent. No pickup, inspection or repair takes place before payment. If the deductible is not paid within this time, the claim is treated as withdrawn and closed, and cannot be re-opened. No new claim will be accepted for the same incident. Where Protega has already incurred inspection or logistics costs, the Customer bears these costs before the product is returned.",
        "Deductible deadline:"
      ),
      clause(
        "Claims reported after 72 hours of the accidental incident may be rejected.",
        "Late reporting:"
      ),
      note("Claim limit: One approved claim per product per Policy Term.", true),
    ],
  },
  {
    kind: "clauses",
    id: "definitions",
    title: "Definitions",
    blocks: [
      clause(
        "The person purchasing an ADIVAA SMART product with the Plan, whose name appears on the invoice.",
        "Customer:"
      ),
      clause("ADIVAA SMART.", "Merchant:"),
      clause(
        "EmbedMike India Private Limited, responsible for Plan management and service delivery.",
        "Administrator / Protega:"
      ),
      clause(
        "The eligible ADIVAA SMART product covered under this Plan and identified in the Certificate of Protection.",
        "Product:"
      ),
      clause(
        "Sudden, unforeseen and unintended physical damage caused by an external event beyond the customer’s control, occurring while the Product is used for its intended purpose in a personal and domestic setting and in accordance with the manufacturer’s instructions.",
        "Accidental Damage:"
      ),
      clause(
        "Any act or omission demonstrating reckless disregard for reasonable care, product safety or manufacturer guidelines.",
        "Gross Negligence:"
      ),
      clause(
        "Use of the Product by the Customer for personal, non-business purposes only.",
        "Personal Use:"
      ),
      clause(
        "Use of the Product for paid work, rental, resale, or by or for a business, institution or office.",
        "Commercial Use:"
      ),
      clause(
        "A service provider officially approved by Protega for inspection, repair, replacement or logistics activities.",
        "Authorised Service Partner:"
      ),
      clause(
        "The duration of coverage selected by the customer at the time of Plan purchase.",
        "Policy Term:"
      ),
      clause(
        "The price paid for the Product as shown on the purchase invoice, after any discounts and including applicable taxes, but excluding shipping or delivery charges, convenience or COD fees, installation charges, EMI interest or fees, and the Plan fee.",
        "Invoice Value:"
      ),
      clause(
        "The Invoice Value of the product. This is the maximum liability under the Plan, across all claims.",
        "Coverage Limit:"
      ),
      clause(
        "The reduction applied to the value of a replacement or Store Credit based on the age of the Product, as set out in clause 1.10.",
        "Depreciation:"
      ),
      clause(
        "One approved claim per product, up to the Coverage Limit.",
        "Claim Limit:"
      ),
      clause(
        "Return Merchandise Authorization issued after claim approval and deductible payment.",
        "RMA:"
      ),
      clause(
        "Where the repair cost exceeds the Invoice Value, or the Product cannot be repaired.",
        "Beyond Economic Repair (BER):"
      ),
      clause(
        "A non-cash voucher or credit usable only on ADIVAA SMART, issued under clause 8.",
        "Store Credit:"
      ),
      clause(
        "A pincode in India where Protega or its Authorised Service Partners provide pickup or service.",
        "Serviceable Location:"
      ),
    ],
  },
  {
    kind: "clauses",
    id: "eligibility",
    title: "Eligibility",
    blocks: [
      note(
        "This Plan shall be applicable only if all of the following conditions are satisfied:"
      ),
      clause(
        "The product has been purchased new and genuine through authorised ADIVAA SMART sales channels in India, and is eligible for the Plan at the time of purchase."
      ),
      clause(
        "The Plan must be purchased together with the product through the Merchant’s eligible sales channel where the Plan is offered."
      ),
      clause(
        "The product is new, unused and free from any physical or functional damage at the time of Plan purchase. Refurbished, open-box, second-hand and returned-and-resold units are not eligible."
      ),
      clause(
        "The product is used for Personal Use only. Commercial Use is excluded."
      ),
      clause(
        "The Customer is responsible for purchasing the Plan for the correct Product. Protega may reject a claim if the model or serial number of the Product claimed does not match the Plan."
      ),
      clause(
        "If a Plan is issued on a product that is not eligible, the Plan is void and the Plan fee is refunded in full."
      ),
      clause(
        "Coverage under this Plan is strictly limited to the primary device unit and does not extend to accessories, attachments, detachable components, or any items subject to periodic replacement."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "what-is-covered",
    title: "What is covered",
    blocks: [
      note(
        "Subject to compliance with all terms, conditions and exclusions, this Plan provides coverage for the following. Every item below is covered only where it results from a sudden, accidental external event.",
        true
      ),
      clause("Accidental drops, falls or impacts causing physical damage"),
      clause("Damage caused by a sudden, accidental external force or collision"),
      clause(
        "Accidental liquid spills or liquid ingress from a single, unintended event"
      ),
      note(
        "Coverage shall apply only where there exists a direct, demonstrable and traceable link between the accidental event and the resulting damage. The deductible applies to claims resolved by repair. Claims resolved by replacement or store credit are subject to depreciation instead (clause 1.10)."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "what-is-not-covered",
    title: "What is not covered",
    blocks: [
      note("This Plan shall not cover, including but not limited to:"),
      clause("Manufacturing defects or inherent product faults."),
      clause("Cosmetic damage that does not impact functionality or usability."),
      clause("Theft, loss or unexplained disappearance of the product."),
      clause(
        "Damage caused by fire, floods, earthquakes, lightning or other natural calamities."
      ),
      clause(
        "Normal wear and tear, corrosion, oxidation or gradual deterioration."
      ),
      clause("Damage arising from improper handling, storage or usage."),
      clause("Unauthorised repairs, modifications or tampering."),
      clause("Software-related issues, firmware errors or data loss."),
      clause("Accessories, peripherals or ancillary items."),
      clause(
        "Components that are subject to regular replacement or consumption."
      ),
      clause("Damage resulting from use outside manufacturer guidelines."),
      clause("Commercial, institutional or professional usage."),
      clause("Damage caused due to misuse, abuse or gross negligence."),
      clause(
        "Any pre-existing damage or defects present prior to Plan purchase."
      ),
      clause(
        "damage from filling, refilling or overfilling, leakage from seals or gaskets, or use of liquids other than those recommended by the manufacturer.",
        "For products that hold, spray or dispense liquid:"
      ),
      clause(
        "battery wear or loss of battery capacity, charging faults, and damage from non-original chargers, cables or power sources, unless caused by a covered accidental event.",
        "For rechargeable products:"
      ),
      clause(
        "Power or electrical surges, voltage fluctuations or short circuits not caused by an external accident."
      ),
      clause(
        "Products whose serial number or label has been altered, defaced or removed."
      ),
      clause(
        "Damage, loss or defects arising before or during shipping and delivery, including transit damage discovered on unboxing. This is handled under ADIVAA SMART’s return and replacement policy."
      ),
      clause(
        "Costs payable under any other warranty, insurance policy or protection plan."
      ),
      clause("Recalls and modifications required by the manufacturer."),
      clause(
        "Further damage caused by continued use of the product after the accidental event."
      ),
      clause(
        "Consequential or incidental losses, such as downtime, loss of use or replacement delays."
      ),
      clause(
        "Claims raised after the Policy Term, or without valid proof of the accident and deductible payment."
      ),
      clause(
        "Any claim involving fraud, misrepresentation or concealment of facts."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "service-procedures",
    title: "Service procedures & limit of liability",
    blocks: [
      clause(
        "Only one (1) approved claim shall be permitted per product during the Policy Term."
      ),
      clause(
        "The total liability of Protega under this Plan shall not exceed the Invoice Value of the product, across all claims."
      ),
      clause(
        "Upon replacement or full settlement, the Plan shall stand terminated automatically."
      ),
      clause(
        "Replacement units may be new, refurbished or functionally equivalent at Protega’s discretion, and may differ in colour or design."
      ),
      clause(
        "All decisions relating to repair, replacement or settlement shall be binding, subject to the dispute resolution clause in clause 11."
      ),
      clause(
        "No cash settlement is made under the Plan. Claims are resolved only by repair, replacement or Store Credit."
      ),
      clause(
        "Replacement and Store Credit are subject to depreciation under clause 1.10, which applies instead of the deductible."
      ),
      clause(
        "Store Credit is valid for 6 months from the date of issue, usable only on ADIVAA SMART, not transferable and not redeemable for cash."
      ),
      clause(
        "Protega may request submission of the damaged product or parts for inspection."
      ),
      clause(
        "Replaced parts, and any product replaced or settled by Store Credit, become the property of Protega."
      ),
      clause(
        "Where the product is handed over incomplete, the value of missing parts is deducted from the resolution."
      ),
      clause(
        "If inspection finds no fault, or finds that the damage is not covered, the claim is rejected and the Customer bears the inspection and logistics costs before the product is returned."
      ),
      clause(
        "If the Customer does not accept delivery or respond within 7 days of Protega’s written notice after repair or rejection, Protega may charge reasonable storage costs from that date, and any further delivery attempts will be charged to the Customer. Protega will send a final written notice before disposal, and if the product is still not collected within 10 days of the first notice, Protega may dispose of or recycle it."
      ),
      clause(
        "Where Protega schedules a pickup, doorstep repair visit, or delivery of a repaired or replacement product, the Customer must be available at the time discussed with the Customer before the visit is scheduled, or reschedule at least 24 hours in advance. If the Customer is unavailable, refuses to accept, or does not provide access, the cost of each further attempt is borne by the Customer and must be paid before the next attempt is scheduled. After two missed attempts, Protega may close the claim as withdrawn or, where the product has already been repaired or replaced, apply the storage and disposal terms above.",
        "Missed appointments:"
      ),
      clause(
        "Protega may reset the device and update its firmware or app during repair, and is not liable for loss of data or settings."
      ),
      clause(
        "If spare parts are no longer available, Protega may resolve the claim by replacement or Store Credit."
      ),
      clause(
        "Protega will make reasonable efforts to meet the timelines in this Plan, but is not liable for delays caused by unavailability of spare parts, logistics disruptions or events beyond its reasonable control."
      ),
      clause(
        "Protega retains the right to recover costs from responsible third parties."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "cancellation",
    title: "Cancellation",
    blocks: [
      clause(
        "The customer may cancel the Plan within seven (7) days of purchase for a full refund, provided that no claim has been initiated."
      ),
      clause(
        "If the product is returned or the order is cancelled, the Plan is cancelled automatically and the Plan fee refunded in full, provided no claim has been raised."
      ),
      clause(
        "The Plan shall become non-refundable once any benefit has been utilised, or after the 7-day period."
      ),
      clause(
        "Protega reserves the right to cancel the Plan in cases of fraud, misrepresentation or material non-disclosure. On such cancellation, the Plan fee is forfeited and Protega may recover all costs incurred on any claim."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "conditions",
    title: "Conditions",
    blocks: [
      clause(
        "This Plan is non-transferable and valid only for the original purchaser."
      ),
      clause("The Plan cannot be renewed, extended or reinstated after expiry."),
      clause(
        "Services under this Plan are available only within India. Pickup and on-site service are available only at Serviceable Locations; elsewhere, Protega may arrange courier pickup or ask the Customer to send the product."
      ),
      clause(
        "Protega reserves the right to request additional verification or documentation at any stage."
      ),
      clause(
        "Any breach of these terms and conditions may result in denial of coverage."
      ),
      clause(
        "All notices and communication are sent to the Customer’s registered email address, mobile number or WhatsApp number and are treated as received when sent."
      ),
      clause(
        "Protega may appoint Authorised Service Partners to perform any part of the Plan."
      ),
      clause(
        "Protega is not liable for any failure or delay caused by events beyond its reasonable control, including natural disasters, epidemics, government action, strikes or disruption to logistics."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "legal-disclosures",
    title: "Legal disclosures",
    blocks: [
      clause(
        "This Plan shall be governed by and construed in accordance with the laws of India. Subject to arbitration, courts at Mumbai have exclusive jurisdiction."
      ),
      clause(
        "Any disputes arising out of or in connection with this Plan shall be resolved through arbitration in accordance with the Arbitration and Conciliation Act, 1996, by a sole arbitrator appointed by mutual agreement or as provided under that Act. The seat and venue of arbitration is Mumbai and the language is English."
      ),
      clause(
        "Nothing in these Terms limits the Customer’s rights or remedies under the Consumer Protection Act, 2019."
      ),
      clause(
        "Protega and ADIVAA SMART shall not be liable for any indirect, incidental, special or consequential damages, including but not limited to loss of data, loss of usage, loss of revenue or business interruption."
      ),
      clause(
        "The Plan and purchase confirmation constitute the entire agreement. If any provision is found invalid, the remaining provisions continue in full force."
      ),
      clause(
        "Customer data is processed as per the Protega Privacy Policy. Personal data, claim photos and videos are shared with ADIVAA SMART and Authorised Service Partners only to administer the Plan, verify claims and prevent fraud. Calls and WhatsApp conversations may be recorded for quality and verification."
      ),
      clause(
        "Where Protega relies on an exclusion to reject a claim, the Customer must show that the claim is covered."
      ),
      clause(
        "The Customer indemnifies Protega against loss caused by the Customer’s fraud, misrepresentation or breach of these Terms."
      ),
      clause(
        "Protega may amend these Terms as needed, following regulatory requirements. Amendments will not reduce cover on Plans already issued, except where required by law."
      ),
      clause(
        "A failure or delay by Protega to enforce any term is not a waiver of that term."
      ),
    ],
  },
  {
    kind: "faq",
    id: "faq",
    title: "FAQ",
    items: [
      {
        q: "What does this Plan cover?",
        a: "This Plan covers accidental physical damage caused by sudden and unforeseen events such as drops, impacts or liquid spills that result in functional impairment.",
      },
      {
        q: "Does this Plan cover manufacturing defects?",
        a: "No. Manufacturing defects are covered under the manufacturer’s warranty.",
      },
      {
        q: "Are all components covered?",
        a: "No. Coverage is limited to the main device unit.",
      },
      {
        q: "Is negligence covered?",
        a: "No. Damage due to misuse or negligence is excluded.",
      },
      { q: "How many claims are allowed?", a: "One approved claim per Plan." },
      {
        q: "Is there a deductible?",
        a: "Yes, 10% of the Invoice Value or ₹500, whichever is lower, payable within 7 days of claim approval.",
      },
      {
        q: "What if repair is not possible?",
        a: "Replacement or store credit will be provided.",
      },
      { q: "Will replacement be new?", a: "It may be new or refurbished." },
      {
        q: "Is liquid damage covered?",
        a: "Yes, if accidental. Leaks from filling, refilling or worn seals are not covered.",
      },
      { q: "When should I report?", a: "Within 72 hours." },
      {
        q: "What documents are needed?",
        a: "Invoice, Plan proof, photos or videos, and incident details.",
      },
      { q: "Can I repair locally?", a: "No." },
      {
        q: "Can I cancel?",
        a: "Yes, within 7 days of purchase if no claim has been initiated.",
      },
      {
        q: "What if I return my product?",
        a: "The Plan is cancelled automatically and the Plan fee is refunded, provided no claim has been raised.",
      },
      {
        q: "Is depreciation applied on a replacement?",
        a: "Yes. Replacement or store credit is reduced by depreciation based on the product’s age, instead of the deductible: 10% up to 6 months, 20% for 6–12 months, 30% for 12–18 months and 40% for 18–24 months. Repairs are not depreciated.",
      },
      {
        q: "Can I get cash instead of a repair?",
        a: "No. Claims are resolved only by repair, replacement or store credit.",
      },
      {
        q: "How long is coverage?",
        a: "Cover starts as soon as you place the order and lasts for 12 or 24 months, depending on the plan selected. It covers only accidental damage that happens after delivery, during normal intended use. Damage in transit is not covered.",
      },
      {
        q: "What happens after a claim?",
        a: "The Plan ends once the claim is resolved.",
      },
    ],
  },
  slaSection,
  {
    kind: "text",
    id: "grievance-redressal",
    title: "Grievance redressal",
    text: "If you are not satisfied with a claim decision or with our service, write to support@protega.ai with your Policy ID. Every complaint receives a ticket number, is acknowledged within 48 hours and is resolved within 30 days.",
  },
  contactSection,
];
